import { type Address, type ValidAddress } from '@/features/account/services/Addresses/types'
import {
  type RetailerETAAddress,
  type RetailerETAAddressSource,
  type RetailerETARequestParams,
  type ExtendedSlotsByDate,
} from '@/features/shop/services/DeliveryWindows/types'

export function hasAvailableTimeslots(timeslots: ExtendedSlotsByDate) {
  return Boolean(Object.values(timeslots).flat().length)
}

type RetailerETASourceInput = {
  userAddressId: number
  address: Address
  guestAddress: ValidAddress | undefined
  zipForZipOnlyUser: string | undefined
  isGuestUser: boolean
}

// the pure body of useQueryRetailerETA, shared with the server prefetch.
export const getRetailerETAQuery = ({
  storeLocationId,
  ...sourceInput
}: RetailerETASourceInput & { storeLocationId: number | null }) => {
  const { etaAddress, source, addressTimezone } = getRetailerETASourceAndAddress(sourceInput)
  const params: RetailerETARequestParams = {
    address: etaAddress,
    store_location_ids: [String(storeLocationId ?? 0)],
    address_timezone: addressTimezone,
    source,
  }
  return { params, enabled: Boolean(storeLocationId && etaAddress.zip_code) }
}

const getRetailerETASourceAndAddress = ({
  userAddressId,
  address,
  guestAddress,
  zipForZipOnlyUser,
  isGuestUser,
}: RetailerETASourceInput): {
  etaAddress: RetailerETAAddress
  source: RetailerETAAddressSource
  addressTimezone: string
} => {
  if (userAddressId && address.id) {
    // Authenticated user with a full saved address
    return {
      source: 'SAVED_ADDRESS',
      etaAddress: {
        street1: address.street1,
        street2: address.street2,
        city: address.city,
        state: address.state,
        zip_code: address.zip_code,
      },
      addressTimezone: address.timezone,
    }
  } else if (zipForZipOnlyUser) {
    // Signed-in user with only a zip code (e.g., Circle 360)
    return {
      source: 'ZIP_CENTER',
      etaAddress: { zip_code: zipForZipOnlyUser },
      addressTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }
  } else if (isGuestUser && guestAddress) {
    // Guest user — use fastly-inferred zip data
    return {
      source: 'IP_GEO_CITY',
      etaAddress: {
        street1: guestAddress.street1,
        street2: guestAddress.street2,
        city: guestAddress.city,
        state: guestAddress.state,
        zip_code: guestAddress.zip_code,
      },
      addressTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }
  } else {
    // Fallback — not enough data to make the request
    return {
      source: 'ZIP_CENTER',
      etaAddress: { zip_code: '' },
      addressTimezone: '',
    }
  }
}
